import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { api } from '../../app/http-common';
import { useCanEdit } from '../../hooks/useFrontegg';
import { STATUS_FORWARD_MAP, STATUS_LABELS, IN_PRODUCTION_STATUS_ORDER, bucketConfigUrl, formatShortDateTime, clientMonogram, shortBucketName } from './constants';
import InProductionRowMenu from './components/InProductionRowMenu';
import InProductionSummary from './components/InProductionSummary';
import ScopeSelect from './components/ScopeSelect';
import StatusTag from './components/StatusTag';
import BucketsLoading from './components/BucketsLoading';
import { SLOTS, SURFACES, saveReturn, peekReturn, clearReturn, restoreScroll } from '../../app/navContext';
import MoveToModal from './components/MoveToModal';
import UndoToast from './components/UndoToast';
import AppModal from '../../components/AppModal/AppModal';
import useBucketData from './hooks/useBucketData';
import './BucketManagerNew.css';

// Spec 8.3 — primary filters. R2 shortens "Sent to publisher" to "Sent": a
// display label, not a new workflow state. "All" stays the name of the filter
// that clears the status selection — the summary line's first figure is called
// "Total", which is a different question.
//
// The counts moved OUT of these chips and into the summary (R1): the summary is
// global while a chip's own count would be filter-scoped, and showing both
// invites the reader to reconcile two numbers that answer different questions.
const FILTERS = [
  { key: 'all', label: 'All', statuses: null },
  { key: 'accepted', label: 'Accepted', statuses: ['accepted'] },
  { key: 'in_work', label: 'In work', statuses: ['in_work'] },
  { key: 'completed', label: 'Completed', statuses: ['completed'] },
  { key: 'sent_to_publisher', label: 'Sent', statuses: ['sent_to_publisher'] },
];

// Who writes the stories on show. §8.1 scoped this screen to Alchemiq-written
// buckets, which is still the default — but it also meant the desk could not
// see what the self-service clients had taken while moving stories between
// them. 16 of 68 active buckets are publisher-written, carrying ~125 chosen
// stories that had no screen at all.
const WRITTEN_BY = [
  { key: 'alchemiq', label: 'Alchemiq' },
  { key: 'publisher', label: 'Client' },
  { key: 'all', label: 'All' },
];

// Only reachable when a publisher-written bucket is in scope: it is the
// terminal state of the publisher forward map. Without it, advancing such a
// story would drop it off the one screen showing it.
const HANDLED_FILTER = {
  key: 'handled_by_publisher', label: 'Handled', statuses: ['handled_by_publisher'],
};

const SORTS = [
  { key: 'recent', label: 'Recently added' },
  // R11 — "bucket", never "section": a bucket need not be a publisher section.
  { key: 'client_bucket_az', label: 'Client, then bucket (A–Z)' },
  { key: 'bucket_az', label: 'Bucket A–Z' },
  { key: 'story_az', label: 'Story A–Z' },
  { key: 'status', label: 'Status' },
];

const PAGE_SIZE = 50;

export default function InProduction() {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const { clients } = useBucketData();

  // §1.10 / §3.3 — restore the view the administrator left when they opened a
  // source bucket.
  const [restore] = useState(() => peekReturn(SURFACES.IN_PRODUCTION)?.state || null);
  const scrollRef = useRef(null);
  // The sticky header's height drives where the table head pins. Measured
  // rather than hard-coded: the block grows a row when the bulk bar appears on
  // selection, and the tabs wrap at narrow widths.
  const stickyHeadRef = useRef(null);
  // §1.1 scroll restore, applied from load() rather than from an effect.
  //
  // The effect-based version never ran — with restore, canEdit, loading, row
  // count, a deadline and a ref guard, something short-circuited silently every
  // time and no scrollTop write was ever issued. Driving it from the one place
  // that knows the rows have just arrived removes every one of those guards.
  //
  // Kept (not nulled) until it lands or the window closes, because this screen
  // loads more than once on arrival and each load blanks the list, which clamps
  // scrollTop back to 0.
  const scrollTargetRef = useRef(restore?.scrollTop || null);
  const scrollDeadlineRef = useRef(restore?.scrollTop ? Date.now() + 8000 : 0);

  const [filter, setFilter] = useState(() => restore?.filter || 'all');
  const [sort, setSort] = useState(() => restore?.sort || 'recent');
  const [search, setSearch] = useState(() => restore?.search || '');
  // Scope: all buckets, one client, or one bucket. null/null means all.
  const [clientId, setClientId] = useState(() => restore?.clientId || null);
  const [bucketId, setBucketId] = useState(() => restore?.bucketId || null);
  const [writtenBy, setWrittenBy] = useState(() => restore?.writtenBy || 'alchemiq');
  const [page, setPage] = useState(() => restore?.page || 1);
  const [data, setData] = useState({
    items: [], total: 0, todayCounts: {}, scopeOptions: [],
  });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set(restore?.selected || []));
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [users, setUsers] = useState([]);
  // The assignable-admin roster is fetched once per mount and can take a moment.
  // Without this the picker rendered "No assignable users loaded" during the
  // wait, which reads as an error rather than as loading.
  const [usersLoading, setUsersLoading] = useState(true);
  const [assignFor, setAssignFor] = useState(null);
  const [comment, setComment] = useState(null);
  const [copyTo, setCopyTo] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!canEdit) navigate('/', { replace: true });
  }, [canEdit, navigate]);

  useEffect(() => {
    const head = stickyHeadRef.current;
    const main = scrollRef.current;
    if (!head || !main || typeof ResizeObserver === 'undefined') return undefined;
    const apply = () => {
      // The block pins 24px (the container's padding-top) ABOVE the content
      // edge, while the column headers' sticky offset is measured FROM that
      // content edge. Subtracting the padding puts the two in the same frame —
      // without it the headers pin one padding-height too low and rows show
      // through the gap. Read rather than hard-coded so a padding change to
      // .pbnew-main cannot silently reopen that gap.
      const padTop = parseFloat(getComputedStyle(main).paddingTop) || 0;
      main.style.setProperty('--pbnew-head-h', `${head.offsetHeight - padTop}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(head);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.getAssignableUsers({ silent: true })
      .then((res) => { if (!cancelled) setUsers(res?.users || res || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setUsersLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // §3.1 — display name for an id. The BACKEND now joins the row's assignee,
  // so item.assignee.{name,email} is the source of truth. This fallback only
  // fires for rows fetched before the deploy or when a user was deleted; it
  // deliberately says "Unknown user" rather than "Assigned", which used to
  // paper over the 403 bug for Editors.
  const userName = useCallback((id) => {
    if (!id) return null;
    const u = users.find((x) => String(x._id) === String(id) || String(x.id) === String(id));
    return u?.name || u?.email || 'Unknown user';
  }, [users]);

  // §8.3 — search covers story, complete bucket name and assignee. Assignee
  // names live on the user records, so matching ids are resolved here and sent
  // to the server, keeping "select all matching" server-authoritative.
  const assigneeIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q))
      .map((u) => String(u._id || u.id));
  }, [search, users]);

  const activeFilters = useMemo(() => ({
    status: [...FILTERS, HANDLED_FILTER].find((f) => f.key === filter)?.statuses?.join(',') || undefined,
    query: search.trim() || undefined,
    assigneeIds,
    clientId: clientId || undefined,
    bucketId: bucketId || undefined,
    writtenBy,
  }), [filter, search, assigneeIds, clientId, bucketId, writtenBy]);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const res = await api.getInProduction({
        ...activeFilters, sort, page, limit: PAGE_SIZE,
      });
      // Freeze the display position of every row this load returned.
      //
      // Pin the order AFTER applying §1.7's status-group priority, not the raw
      // server order — pinning the response directly threw the grouping away
      // and put "Sent to publisher" at the top. So: group on every load, then
      // freeze the result until the next load.
      //
      // Written BEFORE setData so the orderedItems memo, which recomputes on
      // the resulting render, reads the fresh map rather than the previous one.
      const rankOfStatus = (status) => {
        const i = IN_PRODUCTION_STATUS_ORDER.indexOf(status);
        return i === -1 ? IN_PRODUCTION_STATUS_ORDER.length : i;
      };
      const grouped = [...(res.items || [])]
        .map((it, i) => ({ it, i }))
        .sort((a, b) => (rankOfStatus(a.it.status) - rankOfStatus(b.it.status)) || (a.i - b.i));
      const nextRanks = new Map();
      grouped.forEach(({ it }, position) => nextRanks.set(it._id, position));
      pinnedRankRef.current = nextRanks;

      setData({
        items: res.items || [],
        total: res.total || 0,
        todayCounts: res.todayCounts || {},
        // Kept across loads: the selector must still list every bucket while a
        // narrowed request is in flight, or choosing one would empty the list
        // you chose it from.
        scopeOptions: res.scopeOptions?.length ? res.scopeOptions : [],
      });
      if (scrollTargetRef.current && (res.items || []).length) {
        if (Date.now() < scrollDeadlineRef.current) {
          restoreScroll(() => scrollRef.current, scrollTargetRef.current);
        } else {
          scrollTargetRef.current = null;
        }
      }
    } catch {
      setData((d) => ({ ...d, items: [], total: 0, todayCounts: {} }));
    } finally {
      setLoading(false);
    }
  }, [activeFilters, sort, page]);

  // §8.2 — fresh data whenever In Production opens or is revisited.
  // These two effects would otherwise undo a restore on mount: the first clears
  // the checked rows, the second resets the page. Each skips exactly one run.
  // These screens scroll an inner <main class="pbnew-main-full">, NOT the window —
  // window.scrollY is always 0 here, so capturing it restored to the top.
  const keepRestoredPageRef = useRef(!!restore);

  // Clear the selection only when the filters actually CHANGE VALUE.
  //
  // Keying this on `load`'s identity was wrong: activeFilters contains
  // assigneeIds, which is memoised over the async users fetch, so when that
  // request resolves the object identity changes and the effect re-ran — wiping
  // the checked rows out from under the administrator. A restored selection hit
  // the same bug, just more visibly.
  const lastFiltersKeyRef = useRef(null);
  const undoRef = useRef(null);  // §3.2 — status-change Undo payload
  // Display position per row id, frozen at load time so a local status change
  // does not re-sort the list under the operator. Rewritten on every load().
  const pinnedRankRef = useRef(new Map());
  useEffect(() => {
    const key = JSON.stringify({ ...activeFilters, sort, page });
    // First run shows the loader; every subsequent refetch is background so a
    // filter or sort change refreshes rows in place rather than flashing.
    load({ background: lastFiltersKeyRef.current !== null });
    if (lastFiltersKeyRef.current === null || lastFiltersKeyRef.current === key) {
      lastFiltersKeyRef.current = key;
      return;
    }
    lastFiltersKeyRef.current = key;
    setSelected(new Set());
    setSelectAllMatching(false);
  }, [load, activeFilters, sort, page]);

  // The restore has been applied; drop it so a later plain visit starts clean.
  // Clearing is unconditional: this screen is an endpoint of the back journey,
  // so on arrival any pending entry has served its purpose. Leaving it would let
  // an abandoned journey show a stale "Back to In Production" on a later direct
  // URL visit, which §1.10 forbids.
  useEffect(() => { clearReturn(SLOTS.SCREEN); }, []);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load({ background: true }); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  useEffect(() => {
    if (keepRestoredPageRef.current) { keepRestoredPageRef.current = false; return; }
    setPage(1);
  }, [filter, sort, search, clientId, bucketId, writtenBy]);

  const counts = data.todayCounts;

  // One control sets both, so they can never contradict each other — which is
  // the other thing the two separate strips made possible.
  const selectScope = useCallback(({ clientId: c, bucketId: b }) => {
    setClientId(c || null);
    setBucketId(b || null);
  }, []);

  // What the day's figures are counting, said in words above them.
  const scopeLabel = useMemo(() => {
    if (bucketId) {
      const o = data.scopeOptions.find((x) => String(x.bucketId) === String(bucketId));
      return o ? `${o.clientName} · ${o.name}` : 'one bucket';
    }
    if (clientId) {
      const o = data.scopeOptions.find((x) => String(x.clientId) === String(clientId));
      return o ? o.clientName : 'one client';
    }
    return 'all buckets';
  }, [clientId, bucketId, data.scopeOptions]);

  const filters = useMemo(
    () => (writtenBy === 'alchemiq' ? FILTERS : [...FILTERS, HANDLED_FILTER]),
    [writtenBy],
  );

  // Switching scope can strip the active status from under the filter — only a
  // publisher view has "Handled" — so fall back to All rather than querying a
  // status the new scope does not contain.
  const selectWrittenBy = useCallback((key) => {
    setWrittenBy(key);
    if (key === 'alchemiq') setFilter((f) => (f === 'handled_by_publisher' ? 'all' : f));
    // A client or bucket selection may not exist in the other scope either.
    setClientId(null);
    setBucketId(null);
  }, []);

  // Follow-up §1.7 — apply the status-group priority as the PRIMARY sort key,
  // keeping the server's chosen sort as a stable secondary. When only one
  // status is visible (e.g. filter=in_work), the reorder is a no-op.
  //
  // The rank is PINNED at load time (see pinnedRankRef, written in load()).
  // Sorting on the live status instead made a row jump to a different status
  // group the instant an operator advanced it — the row they were working on
  // moved out from under the cursor, which is disorienting on a monitoring
  // screen and makes a second action on the same row a hunt. Positions now
  // settle only on an actual reload: Refresh, a filter/sort/page change, or
  // returning to the tab.
  //
  // A row with no pinned entry (added since the last load) falls back to its
  // live status so it still lands in the right group rather than at the end.
  const orderedItems = useMemo(() => {
    if (!data.items?.length) return data.items || [];
    const rankOf = (status) => {
      const i = IN_PRODUCTION_STATUS_ORDER.indexOf(status);
      return i === -1 ? IN_PRODUCTION_STATUS_ORDER.length : i;
    };
    const pinned = pinnedRankRef.current;
    const key = (it) => (pinned.has(it._id) ? pinned.get(it._id) : rankOf(it.status) * 100000 + 99999);
    // Array.prototype.sort is stable in every supported browser (V8, JSC,
    // Gecko all guarantee it since 2019). Keeps the server's order intact
    // within each group.
    return [...data.items].sort((a, b) => key(a) - key(b));
  }, [data.items]);

  const toggle = (id) => {
    setSelectAllMatching(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = (e) => {
    if (e.target.checked) setSelected(new Set(data.items.map((i) => i._id)));
    else { setSelected(new Set()); setSelectAllMatching(false); }
  };
  const clearSelection = () => { setSelected(new Set()); setSelectAllMatching(false); };
  // §1.10 / §3.3 — opening a source bucket records In Production as the entry
  // surface, so Bucket Stories can offer "Back to In Production" and land the
  // administrator back on the same filter, search, sort and scroll position.
  const openSourceBucket = useCallback((item) => {
    saveReturn({
      surface: SURFACES.IN_PRODUCTION,
      url: '/publisher-buckets-new/in-production',
      label: 'Back to In Production',
      state: {
        filter, sort, search, page, clientId, bucketId, writtenBy,
        selected: [...selected],
        scrollTop: scrollRef.current ? scrollRef.current.scrollTop : 0,
      },
    });
    navigate(`/publisher-buckets-new/bucket/${item.publisherBucketId}`);
  }, [filter, sort, search, page, clientId, bucketId, writtenBy, selected, navigate]);

  const selectionCount = selectAllMatching ? data.total : selected.size;

  // §8.4 / §3.2 / PRD §A3 — advance one status.
  //
  // THE ROW DOES NOT MOVE. It updates its badge in place and keeps its slot
  // until an actual reload (Refresh, a filter/sort/page change, or returning to
  // the tab). That is the whole requirement: an operator working a row should
  // still be looking at it afterwards.
  //
  // This previously predicted the new status from the frontend STATUS_FORWARD_MAP
  // while calling the server with respectWritingResponsibility — and the frontend
  // map has no publisher-written variant. For a publisher-written bucket the
  // server resolves to 'handled_by_publisher' while the client had already
  // decided on the next workflow step, so the badge, the visibility decision and
  // the toast could all three disagree with what was actually stored. The
  // server's resolved status is now authoritative.
  //
  // The optimistic removal + "moved to X" toast that used to run whenever the
  // new status left the active filter are both gone: the row stayed put in
  // several cases while the notice insisted it had moved, which is precisely the
  // defect §A3 describes.
  const advance = useCallback(async (item) => {
    const prevStatus = item.status;
    const title = item.story?.title || `Story ${String(item.storyId).slice(-6)}`;

    // Patch one row in place, leaving its pinned position untouched.
    const setRowStatus = (status) => setData((d) => ({
      ...d,
      items: d.items.map((it) => (
        it._id === item._id
          ? { ...it, status, statusChangedAt: new Date().toISOString() }
          : it
      )),
    }));

    // Optimistic, so the badge responds to the click rather than to the network.
    // Replaced below by whatever the server actually stored.
    setRowStatus(STATUS_FORWARD_MAP[prevStatus] || prevStatus);

    try {
      const res = await api.advanceBucketStoryStatus(
        item._id, { silent: true }, { respectWritingResponsibility: true },
      );
      const nextStatus = res?.item?.status || res?.newStatus || STATUS_FORWARD_MAP[prevStatus] || prevStatus;
      setRowStatus(nextStatus);

      undoRef.current = { kind: 'status', id: item._id, prevStatus };
      setToast({
        // States the new status WITHOUT claiming the row moved — it has not.
        message: `"${title}" is now ${STATUS_LABELS[nextStatus] || nextStatus}.`,
        type: 'success',
        canUndo: true,
      });

      // The day's figures genuinely do shift, so refresh those alone.
      // Deliberately not load(): a full refetch would re-sort and move the row.
      try {
        const counts = await api.getInProduction({ ...activeFilters, sort, page, limit: PAGE_SIZE });
        setData((d) => ({
          ...d,
          total: counts.total ?? d.total,
          todayCounts: counts.todayCounts || d.todayCounts,
        }));
      } catch { /* counts are cosmetic; the row update already landed */ }
    } catch {
      // §A3 — restore the previous badge and say something actionable.
      setRowStatus(prevStatus);
      undoRef.current = null;
      setToast({
        message: `Could not advance "${title}" from ${STATUS_LABELS[prevStatus] || prevStatus}. It is unchanged — try again.`,
        type: 'error',
      });
    }
  }, [activeFilters, sort, page]);

  const handleUndo = useCallback(async () => {
    const ctx = undoRef.current;
    undoRef.current = null;
    setToast(null);
    if (!ctx) return;
    try {
      if (ctx.kind === 'status') {
        await api.changeBucketStoryStatus(ctx.id, ctx.prevStatus, { silent: true });
      }
      setToast({ message: 'Undone.', type: 'success' });
      load({ background: true });
    } catch {
      setToast({ message: 'Could not undo that.', type: 'error' });
    }
  }, [load]);

  // §8.4 — explicit canonical status correction from the overflow.
  //
  // PRD §A3 applies here too: this is a status change in In Production, so the
  // badge updates in place and the row keeps its slot. It used to call
  // load({background:true}), which re-pins every rank and moves the row the
  // administrator just corrected — the same complaint §A3 raises about advance.
  const correctStatus = useCallback(async (item, status) => {
    const prevStatus = item.status;
    const title = item.story?.title || `Story ${String(item.storyId).slice(-6)}`;
    const setRowStatus = (next) => setData((d) => ({
      ...d,
      items: d.items.map((it) => (
        it._id === item._id
          ? { ...it, status: next, statusChangedAt: new Date().toISOString() }
          : it
      )),
    }));

    setRowStatus(status);
    try {
      await api.changeBucketStoryStatus(item._id, status, { silent: true });
      undoRef.current = { kind: 'status', id: item._id, prevStatus };
      setToast({
        message: `Status set to ${STATUS_LABELS[status] || status}.`,
        type: 'success',
        canUndo: true,
      });
      try {
        const counts = await api.getInProduction({ ...activeFilters, sort, page, limit: PAGE_SIZE });
        setData((d) => ({
          ...d,
          total: counts.total ?? d.total,
          todayCounts: counts.todayCounts || d.todayCounts,
        }));
      } catch { /* counts are cosmetic */ }
    } catch {
      setRowStatus(prevStatus);
      undoRef.current = null;
      setToast({
        message: `Could not set "${title}" to ${STATUS_LABELS[status] || status}. It is unchanged — try again.`,
        type: 'error',
      });
    }
  }, [activeFilters, sort, page]);

  const rowAction = useCallback(async (item, action) => {
    try {
      if (action === 'archive') {
        await api.archiveBucketStory(item._id, { silent: true });
        setToast({ message: 'Story archived.', type: 'success' });
      } else if (action === 'delete') {
        await api.removeBucketStory(item._id, { silent: true });
        setToast({ message: 'Story deleted.', type: 'success' });
      } else if (action === 'copy_story') {
        // §8.5 — "Copy story" is title + Alchemiq link to the clipboard. It is
        // explicitly NOT "Copy to…".
        const title = item.story?.title || 'Story';
        const url = item.story?.slug ? `${window.location.origin}/s/${item.story.slug}` : '';
        if (navigator.clipboard) await navigator.clipboard.writeText(url ? `${title}\n${url}` : title);
        setToast({ message: 'Story title and link copied.', type: 'success' });
        return;
      }
      load({ background: true });
    } catch {
      setToast({ message: 'The action could not be completed.', type: 'error' });
    }
  }, [load]);

  const runBulk = useCallback(async (action, assignedAdminUserId) => {
    try {
      const res = await api.inProductionBulk({
        action,
        ids: selectAllMatching ? undefined : [...selected],
        scope: selectAllMatching ? 'filtered' : undefined,
        filters: activeFilters,
        assignedAdminUserId,
      }, { silent: true });
      const n = res?.updated ?? 0;
      // R12 — a bulk assign advances every Accepted story it touches. Saying
      // only "12 stories updated" hides a status change on an unknown number
      // of them, which is the one thing an operator must not have to discover
      // by noticing a count move.
      const started = res?.advanced ?? 0;
      const noun = `stor${n === 1 ? 'y' : 'ies'}`;
      setToast({
        message: started > 0
          ? `${n} ${noun} updated — ${started} started work (In work).`
          : `${n} ${noun} updated.`,
        type: 'success',
      });
      clearSelection();
      load({ background: true });
    } catch {
      setToast({ message: 'The bulk action could not be completed.', type: 'error' });
    }
  }, [selected, selectAllMatching, activeFilters, load]);

  const doAssign = useCallback(async (userId) => {
    const target = assignFor;
    setAssignFor(null);
    if (!target) return;
    try {
      if (target === 'bulk') {
        await runBulk('assign', userId);
        return;
      }

      // R12 — assigning an Accepted story also STARTS it, so this is a status
      // change as well as an assignment, and it has to behave like one.
      //
      // It used to call load({background:true}) and say only "Assigned to X".
      // Two things went wrong with that, both observed on staging: under the
      // Accepted filter the refetch dropped the row out of the result and it
      // vanished under the operator's cursor, and the notice never mentioned
      // that the status had moved at all. That is §A3's complaint — the message
      // and what actually happened disagreeing — with the signs reversed.
      //
      // So: patch the row IN PLACE and keep its slot, exactly as advance()
      // does, and let the message say what really changed. The server's
      // returned status is authoritative; the client does not predict it.
      const prevStatus = target.status;
      const res = await api.assignBucketStoryAdmin(target._id, userId || null, { silent: true });
      const nextStatus = res?.status || res?.item?.status || prevStatus;
      const advanced = nextStatus !== prevStatus;

      setData((d) => ({
        ...d,
        items: d.items.map((it) => (
          it._id === target._id
            ? {
              ...it,
              assignedAdminUserId: userId || null,
              // Cleared rather than rebuilt: the row's display falls back to
              // userName(), which resolves against the roster already loaded.
              assignee: null,
              status: nextStatus,
              statusChangedAt: advanced ? new Date().toISOString() : it.statusChangedAt,
            }
            : it
        )),
      }));

      setToast({
        message: !userId
          ? 'Assignment cleared.'
          : advanced
            ? `Assigned to ${userName(userId)} — started work, now ${STATUS_LABELS[nextStatus] || nextStatus}.`
            : `Assigned to ${userName(userId)}.`,
        type: 'success',
      });

      // A status genuinely moved, so the global figures shift. Refresh those
      // alone — a full load() would re-sort and move the row we just pinned.
      if (advanced) {
        try {
          const counts = await api.getInProduction({ ...activeFilters, sort, page, limit: PAGE_SIZE });
          setData((d) => ({
            ...d,
            total: counts.total ?? d.total,
            todayCounts: counts.todayCounts || d.todayCounts,
            clientBreakdown: counts.clientBreakdown || d.clientBreakdown,
            bucketBreakdown: counts.bucketBreakdown || d.bucketBreakdown,
          }));
        } catch { /* counts are cosmetic; the row update already landed */ }
      }
    } catch {
      setToast({ message: 'Could not update the assignment.', type: 'error' });
    }
  }, [assignFor, runBulk, userName, activeFilters, sort, page]);

  const doCopyTo = useCallback(async (destBucket) => {
    const item = copyTo;
    setCopyTo(null);
    if (!item) return;
    try {
      // §8.5 — Copy to… creates a Suggestion in the target.
      await api.copyBucketStory(item._id, destBucket._id, { silent: true });
      setToast({ message: `Copied to ${destBucket.name} as a suggestion.`, type: 'success' });
    } catch (err) {
      setToast({
        message: err?.response?.status === 409
          ? 'That story is already in the destination bucket.'
          : 'Could not copy the story.',
        type: 'error',
      });
    }
  }, [copyTo]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  if (!canEdit) return null;

  return (
    <div className="pbnew">
      <main className="pbnew-main pbnew-main-full" ref={scrollRef}>
        {/* Title, filter tabs and the bulk bar travel together as one sticky
            block. On a 531-row table everything above the rows scrolled away,
            so by row 30 you could not see which filter was active, reach
            Refresh, or tell the columns apart. The table head sticks directly
            beneath it — see --pbnew-head-h, measured below. */}
        <div className="pbnew-sticky-head" ref={stickyHeadRef}>
        <div className="pbnew-page-head">
          <div className="pbnew-head-titles">
            <h1 className="pbnew-h1">In Production</h1>
            <div className="pbnew-subline">
              <span className="pbnew-dot" />
              {/* The line has to follow the scope: it used to assert
                  "Alchemiq-written" unconditionally, which is now wrong in two
                  of the three views. */}
              {writtenBy === 'alchemiq'
                ? 'Alchemiq-written stories currently in production, across every bucket.'
                : writtenBy === 'publisher'
                  ? 'Stories the self-service clients have taken, across every bucket.'
                  : 'Every story in production, Alchemiq-written and client-written, across every bucket.'}
            </div>
          </div>
          <div className="pbnew-head-actions">
            <button className="pbnew-btn" onClick={() => navigate('/publisher-buckets-new')}>
              <ArrowBackOutlinedIcon style={{ fontSize: 16 }} /> Publisher Buckets
            </button>
          </div>
        </div>

        {/* Today's throughput, for whatever the scope selector is showing. */}
        <InProductionSummary counts={counts} writtenBy={writtenBy} scopeLabel={scopeLabel} />

        {/* R2 — Refresh, the status filters, sort and search on ONE line.
            Three separate rows cost 163px of pinned height for controls that
            are mostly empty space, and every pixel here is a table row the
            operator does not get. */}
        <div className="pbnew-ip-controls">
          <button className="pbnew-btn" onClick={() => load()}>
            <RefreshOutlinedIcon style={{ fontSize: 16 }} /> Refresh
          </button>
          {/* Who writes the stories. First control on the line because it is the
              widest scope: everything to its right narrows within it. */}
          <div className="pbnew-ip-writtenby" role="group" aria-label="Filter by who writes">
            <span className="pbnew-ip-writtenby-label">Written by</span>
            {WRITTEN_BY.map((w) => (
              <button
                key={w.key}
                className={`pbnew-ip-filter ${writtenBy === w.key ? 'active' : ''}`}
                aria-pressed={writtenBy === w.key}
                onClick={() => selectWrittenBy(w.key)}
              >
                {w.label}
              </button>
            ))}
          </div>
          <ScopeSelect
            options={data.scopeOptions}
            clientId={clientId}
            bucketId={bucketId}
            onChange={selectScope}
          />
          <div className="pbnew-ip-filters" role="group" aria-label="Filter by status">
            {filters.map((f) => (
              <button
                key={f.key}
                className={`pbnew-ip-filter ${filter === f.key ? 'active' : ''}`}
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <label className="pbnew-ip-sort">
            <span>Sort by</span>
            <select className="pbnew-select" value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <div className="pbnew-search">
            <SearchOutlinedIcon style={{ fontSize: 16 }} />
            <input
              placeholder="Search story, client, bucket or assignee…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* The bulk bar now appears only when rows ARE selected. "Select all"
            lives in the table's own header checkbox, where the design puts it,
            so an empty 45px row no longer sits pinned above every view. */}
        {selected.size > 0 && (
          <div className="pbnew-selbar">
              <span>{selectionCount} selected</span>
              {!selectAllMatching && selected.size === data.items.length && data.total > data.items.length && (
                <button className="pbnew-linkish" onClick={() => setSelectAllMatching(true)}>
                  Select all {data.total} matching
                </button>
              )}
              {/* §8.6 — Assign, Mark completed, Archive, Delete. No bulk Move. */}
              <div className="pbnew-bulk-actions">
                <button className="pbnew-btn" onClick={() => setAssignFor('bulk')}>
                  <PersonOutlineOutlinedIcon style={{ fontSize: 15 }} /> Assign…
                </button>
                <button className="pbnew-btn" onClick={() => runBulk('mark_completed')}>
                  <TaskAltOutlinedIcon style={{ fontSize: 15 }} /> Mark completed
                </button>
                <button className="pbnew-btn" onClick={() => runBulk('archive')}>
                  <ArchiveOutlinedIcon style={{ fontSize: 15 }} /> Archive
                </button>
                <button className="pbnew-btn" onClick={() => runBulk('delete')}>
                  <DeleteOutlineOutlinedIcon style={{ fontSize: 15 }} /> Delete
                </button>
              </div>
          </div>
        )}
        </div>

        {/* The wrapper only scrolls at narrow widths (see the media query);
            at desktop width it is inert, so the sticky column headers keep
            pinning to the page's own scroll container. */}
        <div className="pbnew-ip-table-wrap">
        <table className="pbnew-table pbnew-ip-table">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input
                  type="checkbox"
                  className="pbnew-checkbox"
                  aria-label="Select all rows on this page"
                  title="Select all rows on this page"
                  checked={data.items.length > 0 && selected.size === data.items.length}
                  onChange={toggleAll}
                />
              </th>
              <th>Story</th>
              <th style={{ width: 190 }}>Bucket</th>
              <th style={{ width: 130 }}>Status</th>
              <th style={{ width: 150 }}>Assignee</th>
              <th style={{ width: 120 }}>Updated</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="pbnew-empty"><BucketsLoading /></td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="pbnew-empty">Nothing in production for this view.</td></tr>
            )}
            {/* Follow-up §1.7 — stable reorder by status-group priority so
                Accepted and In work sit above Completed. The server's chosen
                sort (Recently added / A–Z / Status) is preserved as the
                secondary order within each group. */}
            {!loading && orderedItems.map((item) => (
              <tr key={item._id}>
                <td>
                  <input
                    type="checkbox"
                    className="pbnew-checkbox"
                    checked={selected.has(item._id)}
                    onChange={() => toggle(item._id)}
                  />
                </td>
                <td>
                  {/* §8.2 — no suggestion/list numbers in production. R9 — the
                      title wraps to at most two lines; the full text stays
                      reachable via the tooltip and the story page itself, so
                      nothing is lost to the clamp. */}
                  <div className="pbnew-td-title pbnew-ip-title-row">
                    <span className="pbnew-ip-title" title={item.story?.title || undefined}>
                    {item.story?.slug ? (
                      // Follow-up §1.5 — story title opens Item Details in a
                      // NEW tab so the monitoring workspace, its filter, sort,
                      // selection and scroll all stay put. `rel=noreferrer`
                      // strips the referrer per standard opener hygiene.
                      <a
                        href={`/story/${item.story.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {item.story?.title || `Story ${item.storyId?.toString().slice(-6)}`}
                      </a>
                    ) : !item.story ? (
                      // Null story = the row points at a GroupSubject this
                      // database does not have. Expected on staging (bucket
                      // rows are mirrored from production, which has its own
                      // database); in production it means a deleted story.
                      <>
                        <span className="pbnew-story-missing">Story unavailable</span>
                        <span
                          className="pbnew-missing-tag"
                          title={`References story ${item.storyId}, which is not in this database.`}
                        >
                          not in this database
                        </span>
                      </>
                    ) : (item.story?.title || `Story ${item.storyId?.toString().slice(-6)}`)}
                    </span>
                    {/* Outside the clamp on purpose — a long title must never
                        be able to push the Client comment affordance out of
                        view (PRD §4: comment access is retained). */}
                    {item.clientFeedbackComment && (
                      <button
                        type="button"
                        className="pbnew-comment-btn"
                        onClick={() => setComment(item)}
                        title="Client comment"
                      >
                        <ChatBubbleOutlineOutlinedIcon style={{ fontSize: 13 }} />
                      </button>
                    )}
                  </div>
                  <div className="pbnew-td-meta">
                    {(item.story?.articleCount ?? item.articleCountSnapshot ?? 0)} articles
                  </div>
                </td>
                <td>
                  {/* R7 — monogram, then client above bucket. The monogram is a
                      reading aid for scanning a mixed-client list, not a client
                      identity or logo system: `clientMonogram` falls back to "?" when
                      a name yields nothing. */}
                  <div className="pbnew-ip-bucket">
                    <span className="pbnew-ip-mono" aria-hidden="true">
                      {clientMonogram(item.bucket?.clientId?.name || '')}
                    </span>
                    <span className="pbnew-ip-bucket-names">
                      <span className="pbnew-ip-client">{item.bucket?.clientId?.name || '—'}</span>
                      {/* §8.2 — complete bucket names. */}
                      <button
                        className="pbnew-linkish pbnew-ip-bucket-name"
                        onClick={() => openSourceBucket(item)}
                        title={`${item.bucket?.name || ''} — open in Bucket Stories`}
                      >
                        {/* Same de-duplication as the chips: the client is on
                            the line above, so "Israel Hayom / Israel Hayom
                            Tourism" says it twice. Full name in the tooltip. */}
                        {shortBucketName(item.bucket?.name || '', item.bucket?.clientId?.name || '') || '—'}
                      </button>
                    </span>
                  </div>
                </td>
                <td>
                  {/* R8 — bordered, lightly tinted tag with an icon and text.
                      Still ADVANCES on click (follow-up §1.6 / PRD §A3): the
                      status row above is what filters. */}
                  <StatusTag status={item.status} onClick={() => advance(item)} />
                </td>
                <td>
                  <button className="pbnew-linkish" onClick={() => setAssignFor(item)}>
                    <PersonOutlineOutlinedIcon style={{ fontSize: 13, verticalAlign: -2 }} />{' '}
                    {item.assignee?.name || item.assignee?.email || userName(item.assignedAdminUserId) || 'Unassigned'}
                  </button>
                </td>
                <td className="pbnew-td-meta">{formatShortDateTime(item.statusChangedAt || item.updatedAt)}</td>
                <td>
                  <InProductionRowMenu
                    item={item}
                    onCorrectStatus={correctStatus}
                    onCopyTo={() => setCopyTo(item)}
                    onAction={rowAction}
                    configUrl={bucketConfigUrl(item.publisherBucketId)}
                    onOpenBucket={() => openSourceBucket(item)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {data.total > PAGE_SIZE && (
          <div className="pbnew-pager">
            <button className="pbnew-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeftOutlinedIcon style={{ fontSize: 16 }} /> Previous
            </button>
            <span>Page {page} of {totalPages} · {data.total} stories</span>
            <button className="pbnew-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next <ChevronRightOutlinedIcon style={{ fontSize: 16 }} />
            </button>
          </div>
        )}
      </main>

      <AppModal
        isOpen={!!assignFor}
        title={assignFor === 'bulk' ? `Assign ${selectionCount} stories` : 'Assign story'}
        onCancel={() => setAssignFor(null)}
        maxWidth={420}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button className="pbnew-btn" onClick={() => setAssignFor(null)}>Cancel</button>
            <button className="pbnew-btn primary" onClick={() => doAssign(null)}>Clear assignment</button>
          </div>
        )}
      >
        <div className="pbnew-scope">
          <div className="pbnew-assign-list">
            {usersLoading && <BucketsLoading size={120} />}
            {!usersLoading && users.map((u) => (
              <button key={u._id || u.id} className="pbnew-menu-item" onClick={() => doAssign(String(u._id || u.id))}>
                {u.name || u.email}
              </button>
            ))}
            {/* Only a genuine empty RESULT says "none" — never the loading state. */}
            {!usersLoading && users.length === 0 && (
              <div className="pbnew-drop-hint">No assignable users found.</div>
            )}
          </div>
        </div>
      </AppModal>

      <AppModal
        isOpen={!!comment}
        title="Client comment"
        onCancel={() => setComment(null)}
        cancelLabel="Close"
        maxWidth={460}
      >
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {comment?.clientFeedbackComment}
        </p>
      </AppModal>

      {copyTo && (
        <MoveToModal
          isOpen
          mode="copy"
          count={1}
          incoming={[]}
          clients={clients}
          currentBucketId={copyTo.publisherBucketId}
          onSelect={(destBucket) => doCopyTo(destBucket)}
          onClose={() => setCopyTo(null)}
        />
      )}

      <UndoToast toast={toast} onUndo={handleUndo} onDismiss={() => setToast(null)} />
    </div>
  );
}
